import React, { useState, useEffect, useCallback } from "react";
import { FiEdit2, FiTrash2, FiPlus, FiList, FiLoader, FiFilter, FiX } from "react-icons/fi";
import ThemeMaker from "../../components/thememaker";
import { baseUrlDefiner } from "../../utils/config";

/* =================================================================
   ENDPOINTS
   - getsellerlist / getProtypeList / get-product-list live on the
     PHP admin API, so they use baseUrlDefiner() to resolve the
     right domain per environment (prod / preprod / wac-preprod...).
   - /admin/api/margins lives on the Node backend, so it's wired to
     NodebaseUrlDefiner() (baseNodeapiUrl), not the PHP admin API.
   ================================================================= */

const PHP_API_BASE =
    window.location.hostname === "localhost"
        ? "https://preprodshikharapp.hulsecure.in/public/index.php/api"
        : baseUrlDefiner();
const NODE_API_BASE = "https://sr.thesellerapp.com";
const MARGIN_API_BASE = `${NODE_API_BASE}/admin/api/margins`;

const ENDPOINTS = {
    providers: `${PHP_API_BASE}/getsellerlist`,
    categories: `${PHP_API_BASE}/getProtypeList`,
    items: (providerId) => `${PHP_API_BASE}/get-product-list?provider_id=${encodeURIComponent(providerId)}`,
    marginsList: (query) => `${MARGIN_API_BASE}${query ? `?${query}` : ""}`,
    marginById: (id) => `${MARGIN_API_BASE}/${id}`,
};

// Supported filters per API docs: level, provider_id, buyer_app_id, product_type_name, item_id, status
const FILTER_KEYS = ["provider_id", "level", "buyer_app_id", "product_type_name", "item_id", "status"];

const emptyFilters = {
    provider_id: "",
    level: "",
    buyer_app_id: "",
    product_type_name: "",
    item_id: "",
    status: "",
};

function buildQueryString(filters) {
    const params = new URLSearchParams();
    FILTER_KEYS.forEach((key) => {
        const val = filters[key];
        if (val !== "" && val !== undefined && val !== null) params.set(key, val);
    });
    return params.toString();
}

function getAuthToken() {
    return sessionStorage.getItem("us_st_d") || "";
}

function authHeaders() {
    const token = getAuthToken();
    return { "x-custom-authorization": `Bearer ${token}` };
}

async function apiCall(url, { method = "GET", body } = {}) {
    const res = await fetch(url, {
        method,
        headers: {
            Accept: "application/json",
            ...(body ? { "Content-Type": "application/json" } : {}),
            ...authHeaders(),
        },
        body: body ? JSON.stringify(body) : undefined,
    });
    const json = await res.json().catch(() => null);
    if (!res.ok) {
        throw new Error(json?.message || `${method} ${url} → HTTP ${res.status}`);
    }
    return json;
}

function unwrapArray(raw) {
    if (Array.isArray(raw)) return raw;
    if (!raw || typeof raw !== "object") return [];
    for (const key of ["data", "result", "results", "list"]) {
        if (Array.isArray(raw[key])) return raw[key];
    }
    return [];
}

// Single-record responses come back as { status, data: {...} } per the
// "get one margin by id" API — unwrap that shape specifically.
function unwrapRecord(raw) {
    if (!raw || typeof raw !== "object") return null;
    if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) return raw.data;
    return raw;
}

function normalizeProviders(raw) {
    return unwrapArray(raw)
        .filter((r) => r.status !== false)
        .map((r) => ({
            value: r.provider_id,
            label: r.name ? `${r.company_name} — ${r.name}` : r.company_name,
        }))
        .filter((o) => o.value);
}

function normalizeCategories(raw) {
    return unwrapArray(raw)
        .filter((r) => r.status !== false)
        .map((r) => ({ value: r.name, label: r.name }))
        .filter((o) => o.value);
}

function normalizeItems(raw) {
    // Response shape for get-product-list wasn't confirmed — trying the
    // common candidate keys. Adjust here once you see the real payload.
    return unwrapArray(raw)
        .map((r) => ({
            value: String(r.item_id ?? r.product_id ?? r.id ?? ""),
            label: r.item_name ?? r.product_name ?? r.name ?? r.title ?? "Unnamed",
        }))
        .filter((o) => o.value);
}

/* ================================================================= */

const LEVELS = [
    { value: "provider", label: "Provider", description: "Applies to all products from this provider" },
    { value: "category", label: "Category", description: "Applies to a specific product type" },
    { value: "item", label: "Item", description: "Applies to one specific item" },
];

const STATUS_FILTER_OPTIONS = [
    { value: "", label: "All statuses" },
    { value: "true", label: "Active" },
    { value: "false", label: "Disabled" },
];

const emptyForm = {
    id: null,
    provider_id: "",
    level: "provider",
    buyer_app_id: "",
    product_type_name: "",
    item_id: "",
    ondc_bff_item_id: "",
    channel_margin_type: "percent",
    channel_margin_value: "",
    title: "",
    domain: "",
    city: "",
    valid_from: "",
    valid_to: "",
    status: true,
};

function fmtValue(v, type) {
    const n = Number(v);
    return type === "percent" ? `${n}%` : `₹${n.toFixed(2)}`;
}
function cap(s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}

export default function ChannelMarginsAdmin() {
    const [tab, setTab] = useState("create");
    const [margins, setMargins] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState("");
    // Domain/city/title/valid-from/valid-to aren't part of the margins
    // API schema, so they're kept client-side only, keyed by margin id.
    // They will NOT survive a page refresh until the backend adds columns.
    const [localExtras, setLocalExtras] = useState({});

    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [toast, setToast] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [editLoading, setEditLoading] = useState(false);

    const [providers, setProviders] = useState([]);
    const [providersState, setProvidersState] = useState("idle");
    const [categories, setCategories] = useState([]);
    const [categoriesState, setCategoriesState] = useState("idle");
    const [items, setItems] = useState([]);
    const [itemsState, setItemsState] = useState("idle");

    // ---------- list filters ----------
    const [filters, setFilters] = useState(emptyFilters);
    const [showFilters, setShowFilters] = useState(false);

    const showToast = (message) => {
        setToast(message);
        setTimeout(() => setToast(null), 2600);
    };

    /* ---------- dropdown data ---------- */

    const loadProviders = useCallback(() => {
        setProvidersState("loading");
        apiCall(ENDPOINTS.providers)
            .then((raw) => { setProviders(normalizeProviders(raw)); setProvidersState("done"); })
            .catch((err) => { console.error("getsellerlist failed:", err); setProvidersState("error"); });
    }, []);

    const loadCategories = useCallback(() => {
        setCategoriesState("loading");
        apiCall(ENDPOINTS.categories, { method: "POST" })
            .then((raw) => { setCategories(normalizeCategories(raw)); setCategoriesState("done"); })
            .catch((err) => { console.error("getProtypeList failed:", err); setCategoriesState("error"); });
    }, []);

    const loadItems = useCallback((providerId) => {
        setItemsState("loading");
        apiCall(ENDPOINTS.items(providerId))
            .then((raw) => { setItems(normalizeItems(raw)); setItemsState("done"); })
            .catch((err) => { console.error("get-product-list failed:", err); setItemsState("error"); });
    }, []);

    useEffect(() => { loadProviders(); }, [loadProviders]);

    useEffect(() => {
        if (form.level === "category" && categoriesState === "idle") loadCategories();
    }, [form.level, categoriesState, loadCategories]);

    useEffect(() => {
        if (form.level === "item" && form.provider_id) loadItems(form.provider_id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.level, form.provider_id]);

    useEffect(() => {
        if (showFilters && categoriesState === "idle") loadCategories();
    }, [showFilters, categoriesState, loadCategories]);

    useEffect(() => {
        if (showFilters && filters.provider_id) loadItems(filters.provider_id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showFilters, filters.provider_id]);


    /* ---------- margins list ---------- */

    const fetchMargins = useCallback((activeFilters) => {
        setListLoading(true);
        setListError("");
        const query = buildQueryString(activeFilters ?? filters);
        apiCall(ENDPOINTS.marginsList(query))
            .then((json) => setMargins(unwrapArray(json)))
            .catch((err) => { console.error("list margins failed:", err); setListError(err.message); })
            .finally(() => setListLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    useEffect(() => { fetchMargins(emptyFilters); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const setFilter = (key) => (e) => {
        setFilters((prev) => ({ ...prev, [key]: e.target.value }));
    };

    const applyFilters = () => fetchMargins(filters);

    const clearFilters = () => {
        setFilters(emptyFilters);
        fetchMargins(emptyFilters);
    };

    const activeFilterCount = FILTER_KEYS.filter((k) => filters[k] !== "").length;

    /* ---------- form ---------- */

    const validate = (f) => {
        const e = {};
        if (!f.level) e.level = "Scope level is required";
        if (!f.provider_id?.trim()) e.provider_id = "Provider is required";
        if (f.level === "category" && !f.product_type_name?.trim())
            e.product_type_name = "Product type is required for category scope";
        if (f.level === "item" && !f.item_id?.trim())
            e.item_id = "Item is required for item scope";
        if (!["percent", "amount"].includes(f.channel_margin_type))
            e.channel_margin_type = "Invalid margin type";
        if (f.channel_margin_value === "" || isNaN(Number(f.channel_margin_value)))
            e.channel_margin_value = "Enter a valid numeric margin value";
        return e;
    };

    const resetForm = () => {
        setForm(emptyForm);
        setErrors({});
    };

    const handleSubmit = async () => {
        const e = validate(form);
        setErrors(e);
        if (Object.keys(e).length) return;

        const basePayload = {
            provider_id: form.provider_id.trim(),
            level: form.level,
            buyer_app_id: form.buyer_app_id.trim() || null,
            product_type_name: form.level === "category" ? form.product_type_name.trim() : null,
            item_id: form.level === "item" ? form.item_id.trim() : null,
            channel_margin_type: form.channel_margin_type,
            channel_margin_value: Number(form.channel_margin_value),
        };

        setSubmitting(true);
        try {
            let saved;
            if (form.id) {
                const updatePayload = {
                    buyer_app_id: basePayload.buyer_app_id,
                    product_type_name: basePayload.product_type_name,
                    item_id: basePayload.item_id,
                    channel_margin_type: basePayload.channel_margin_type,
                    channel_margin_value: basePayload.channel_margin_value,
                    status: form.status,
                };
                const res = await apiCall(ENDPOINTS.marginById(form.id), {
                    method: "PUT",
                    body: updatePayload,
                });
                saved = unwrapRecord(res);
                showToast(res.message || "Margin updated");
            } else {
                const res = await apiCall(MARGIN_API_BASE, { method: "POST", body: basePayload });
                saved = unwrapRecord(res);
                showToast(res.message || "Margin saved");
            }

            if (saved?.id) {
                setLocalExtras((prev) => ({
                    ...prev,
                    [saved.id]: {
                        title: form.title.trim(),
                        domain: form.domain.trim(),
                        city: form.city.trim(),
                        valid_from: form.valid_from,
                        valid_to: form.valid_to,
                    },
                }));
            }

            resetForm();
            setTab("list");
            fetchMargins();
        } catch (err) {
            console.error(err);
            showToast(err.message || "Something went wrong");
        } finally {
            setSubmitting(false);
        }
    };

    // Pulls the latest record from GET /admin/api/margins/:id before opening
    // the edit form, so edit always reflects the source of truth (including
    // fields like ondc_bff_item_id that the list row may not carry).
    const handleEdit = async (m) => {
        setTab("create");
        setEditLoading(true);
        setErrors({});

        // Show something immediately from the row we already have, then
        // replace it once the fresh record comes back.
        const extra = localExtras[m.id] || {};
        const applyRecord = (record) => {
            setForm({
                id: record.id,
                provider_id: record.provider_id,
                level: record.level,
                buyer_app_id: record.buyer_app_id || "",
                product_type_name: record.product_type_name || "",
                item_id: record.item_id || "",
                ondc_bff_item_id: record.ondc_bff_item_id || "",
                channel_margin_type: record.channel_margin_type,
                channel_margin_value: String(Number(record.channel_margin_value)),
                title: extra.title || "",
                domain: extra.domain || "",
                city: extra.city || "",
                valid_from: extra.valid_from || "",
                valid_to: extra.valid_to || "",
                status: record.status,
            });
        };

        applyRecord(m);

        try {
            const res = await apiCall(ENDPOINTS.marginById(m.id));
            const fresh = unwrapRecord(res);
            if (fresh) applyRecord(fresh);
        } catch (err) {
            console.error("get margin by id failed:", err);
            showToast("Couldn't refresh latest margin details — showing last known values");
        } finally {
            setEditLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await apiCall(ENDPOINTS.marginById(id), { method: "DELETE" });
            showToast("Channel margin deleted");
            fetchMargins();
        } catch (err) {
            showToast(err.message || "Delete failed");
        }
    };

    const handleToggle = async (m) => {
        // optimistic update so the switch responds immediately
        setMargins((prev) => prev.map((row) => (row.id === m.id ? { ...row, status: !row.status } : row)));
        try {
            await apiCall(ENDPOINTS.marginById(m.id), { method: "PUT", body: { status: !m.status } });
        } catch (err) {
            // revert on failure
            setMargins((prev) => prev.map((row) => (row.id === m.id ? { ...row, status: m.status } : row)));
            showToast(err.message || "Update failed");
        }
    };

    const set = (key) => (e) => {
        setForm((prev) => ({ ...prev, [key]: e.target.value }));
        if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    return (
        <ThemeMaker>
            <div className="channel-margin-page">
                <style>{`
                    .channel-margin-page { min-height: calc(100vh - 70px); width: 100%; background: #f7f8fc; font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: #111827; padding: 20px 24px 32px; }
                    .cm-container { width: 100%; max-width: 1450px; margin: 0 auto; }
                    .cm-header { display: flex; flex-direction: column; align-items: flex-start; gap: 12px; margin-bottom: 18px; background: #eef0fb; border: 1px solid #e3e7fb; border-radius: 12px; padding: 18px 22px; }
                    .cm-title { margin: 0; font-size: 16px; line-height: 1.2; font-weight: 700; color: #001ad5; letter-spacing: -0.2px; }
                    .cm-tabs { display: inline-flex; gap: 10px; }
                    .cm-tab { border: none; background: #4a5fe8; color: white; padding: 9px 16px; border-radius: 7px; font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.2s ease; }
                    .cm-tab:hover { background: #3d4fd6; }
                    .cm-tab.active { background: #001ad5; box-shadow: 0 2px 6px rgba(0, 26, 213, 0.35); }
                    .cm-card { background: white; border: 1px solid #e5e7eb; border-radius: 12px; box-shadow: 0 2px 8px rgba(15, 23, 42, 0.03); overflow: hidden; }
                    .cm-card-header { padding: 16px 20px; border-bottom: 1px solid #edf0f4; display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
                    .cm-section-title { margin: 0; font-size: 14px; font-weight: 700; color: #1f2937; }
                    .cm-section-description { margin: 3px 0 0; color: #9ca3af; font-size: 12px; }
                    .cm-card-body { padding: 20px; }
                    .cm-form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px 22px; }
                    .cm-form-grid-4 { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
                    .cm-form-grid-6 { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 14px; }
                    .cm-field { min-width: 0; }
                    .cm-label { display: block; font-size: 12px; font-weight: 600; color: #374151; margin-bottom: 5px; }
                    .cm-required { color: #dc2626; margin-left: 2px; }
                    .cm-input { width: 100%; height: 38px; padding: 0 11px; border-radius: 7px; border: 1px solid #d9dde5; background: white; color: #111827; font-size: 13px; outline: none; box-sizing: border-box; transition: all 0.2s ease; }
                    .cm-input:hover { border-color: #b9bfcc; }
                    .cm-input:focus { border-color: #6674e8; box-shadow: 0 0 0 3px rgba(102, 116, 232, 0.10); }
                    .cm-input:disabled { background: #f3f4f6; color: #6b7280; cursor: not-allowed; }
                    .cm-input.error { border-color: #dc2626; }
                    .cm-error { color: #dc2626; font-size: 11px; margin-top: 4px; }
                    .cm-help { color: #9ca3af; font-size: 11px; margin-top: 4px; }
                    .cm-divider { height: 1px; background: #edf0f4; margin: 20px 0; }
                    .cm-section-heading { margin-bottom: 14px; }
                    .cm-section-heading-title { font-size: 13px; font-weight: 700; color: #1f2937; margin: 0; }
                    .cm-section-heading-text { font-size: 11px; color: #9ca3af; margin: 3px 0 0; }
                    .cm-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 22px; padding-top: 16px; border-top: 1px solid #edf0f4; }
                    .cm-btn { height: 38px; padding: 0 16px; border-radius: 7px; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 6px; transition: all 0.2s ease; }
                    .cm-btn:disabled { opacity: 0.6; cursor: not-allowed; }
                    .cm-btn-secondary { border: 1px solid #d9dde5; background: white; color: #4b5563; }
                    .cm-btn-secondary:hover { background: #f9fafb; }
                    .cm-btn-primary { border: none; background: #4f5fde; color: white; box-shadow: 0 3px 8px rgba(79, 95, 222, 0.20); }
                    .cm-btn-primary:hover { background: #4352cf; transform: translateY(-1px); }
                    .cm-btn-ghost { border: 1px solid #d9dde5; background: white; color: #4b5563; height: 34px; padding: 0 12px; font-size: 12px; }
                    .cm-btn-ghost:hover { background: #f9fafb; }
                    .cm-filter-toggle { position: relative; }
                    .cm-filter-badge { position: absolute; top: -6px; right: -6px; background: #001ad5; color: white; font-size: 10px; font-weight: 700; border-radius: 999px; min-width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; padding: 0 3px; }
                    .cm-filter-panel { padding: 16px 20px; border-bottom: 1px solid #edf0f4; background: #fafbff; }
                    .cm-filter-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
                    .cm-table-wrapper { overflow-x: auto; -webkit-overflow-scrolling: touch; }
                    .cm-table { width: 100%; min-width: 640px; border-collapse: collapse; }
                    .cm-table th { background: #001ad5; color: #ffffff; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; padding: 11px 16px; text-align: left; border-bottom: 1px solid #001ad5; white-space: nowrap; }
                    .cm-table td { padding: 12px 16px; border-bottom: 1px solid #edf0f4; vertical-align: middle; font-size: 13px; }
                    .cm-table tbody tr { transition: background 0.15s ease; }
                    .cm-table tbody tr:hover { background: #fafbff; }
                    .cm-table tbody tr:last-child td { border-bottom: none; }
                    .cm-scope-title { font-weight: 600; color: #1f2937; font-size: 13px; }
                    .cm-scope-meta { color: #9ca3af; font-size: 11px; margin-top: 4px; }
                    .cm-value { font-weight: 700; color: #9a6b00; }
                    .cm-status { display: inline-flex; align-items: center; gap: 5px; padding: 4px 9px; border-radius: 999px; font-size: 10px; font-weight: 700; white-space: nowrap; }
                    .cm-status.active { background: #e9f8ef; color: #188451; }
                    .cm-status.disabled { background: #f3f4f6; color: #9ca3af; }
                    .cm-status-dot { width: 5px; height: 5px; border-radius: 50%; background: currentColor; }
                    .cm-actions-cell { display: flex; align-items: center; justify-content: flex-end; gap: 8px; }
                    .cm-icon-btn { width: 32px; height: 32px; border-radius: 7px; border: none; background: transparent; color: #6b7280; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s ease; flex-shrink: 0; }
                    .cm-icon-btn:hover { background: #f0f1f7; color: #4f5fde; }
                    .cm-icon-btn.danger:hover { background: #fff0f0; color: #dc2626; }
                    .cm-empty { text-align: center; padding: 40px 20px; color: #9ca3af; }
                    .cm-empty-icon { width: 42px; height: 42px; margin: 0 auto 10px; border-radius: 10px; background: #f1f3f8; display: flex; align-items: center; justify-content: center; color: #9ca3af; }
                    .cm-toast { position: fixed; right: 24px; bottom: 24px; background: #111827; color: white; padding: 12px 16px; border-radius: 9px; font-size: 13px; font-weight: 500; box-shadow: 0 8px 25px rgba(0,0,0,0.18); z-index: 9999; max-width: calc(100vw - 48px); }
                    .cm-select-status { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
                    @keyframes cmspin { to { transform: rotate(360deg); } }
                    .cm-spin { animation: cmspin 0.8s linear infinite; }

                    /* ---------- toggle switch (replaces the old power icon button) ---------- */
                    .cm-toggle { position: relative; display: inline-flex; align-items: center; width: 36px; height: 20px; flex-shrink: 0; }
                    .cm-toggle input { position: absolute; opacity: 0; width: 100%; height: 100%; margin: 0; cursor: pointer; z-index: 1; }
                    .cm-toggle-track { position: absolute; inset: 0; background: #d9dde5; border-radius: 999px; transition: background 0.2s ease; }
                    .cm-toggle-thumb { position: absolute; top: 2px; left: 2px; width: 16px; height: 16px; border-radius: 50%; background: white; box-shadow: 0 1px 2px rgba(0,0,0,0.25); transition: transform 0.2s ease; }
                    .cm-toggle input:checked ~ .cm-toggle-track { background: #188451; }
                    .cm-toggle input:checked ~ .cm-toggle-thumb { transform: translateX(16px); }
                    .cm-toggle input:disabled ~ .cm-toggle-track { opacity: 0.5; cursor: not-allowed; }
                    .cm-toggle input:focus-visible ~ .cm-toggle-track { box-shadow: 0 0 0 3px rgba(102, 116, 232, 0.25); }

                    /* ---------- Responsive ---------- */
                    @media (max-width: 1100px) {
                        .cm-form-grid-4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
                        .cm-form-grid-6 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
                    }
                    @media (max-width: 900px) {
                        .cm-form-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px 20px; }
                    }
                    @media (max-width: 800px) {
                        .channel-margin-page { padding: 18px 14px 28px; }
                        .cm-header { align-items: stretch; padding: 16px 18px; }
                        .cm-title { font-size: 13px; }
                        .cm-tabs { width: 100%; }
                        .cm-tab { flex: 1; justify-content: center; padding: 10px 8px; }
                        .cm-form-grid { grid-template-columns: 1fr; gap: 18px; }
                        .cm-form-grid-4 { grid-template-columns: 1fr; gap: 18px; }
                        .cm-form-grid-6 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
                        .cm-card-header { flex-wrap: wrap; gap: 12px; }
                        .cm-card-header .cm-btn { width: 100%; justify-content: center; }
                        .cm-actions { flex-direction: column-reverse; }
                        .cm-actions .cm-btn { width: 100%; }
                        .cm-icon-btn { width: 36px; height: 36px; }
                    }
                    @media (max-width: 500px) {
                        .channel-margin-page { padding: 14px 10px 24px; }
                        .cm-card-header, .cm-card-body, .cm-filter-panel { padding: 16px 14px; }
                        .cm-title { font-size: 18px; }
                        .cm-input { height: 44px; font-size: 14px; }
                        .cm-toast { left: 12px; right: 12px; bottom: 16px; max-width: none; text-align: center; }
                        .cm-form-grid-6 { grid-template-columns: 1fr; }
                    }
                `}</style>

                <div className="cm-container">
                    <div className="cm-header">
                        <h1 className="cm-title">{tab === "create" ? (form.id ? "Edit Channel Margin" : "Create Channel Margin") : "Channel Margin List"}</h1>

                        <div className="cm-tabs">
                            <button className={`cm-tab ${tab === "create" ? "active" : ""}`} onClick={() => { resetForm(); setTab("create"); }}>
                                Create Channel Margin
                            </button>
                            <button className={`cm-tab ${tab === "list" ? "active" : ""}`} onClick={() => setTab("list")}>
                                Channel Margin List
                            </button>
                        </div>
                    </div>

                    {tab === "create" ? (
                        <div className="cm-card">
                            <div className="cm-card-body">
                                {editLoading && (
                                    <div className="cm-help" style={{ marginBottom: 14, display: "flex", alignItems: "center", gap: 6 }}>
                                        <FiLoader className="cm-spin" size={12} /> Refreshing latest margin details…
                                    </div>
                                )}
                                <div className="cm-form-grid">
                                    <Field label="Provider" required error={errors.provider_id}>
                                        <SelectField
                                            value={form.provider_id}
                                            onChange={set("provider_id")}
                                            status={providersState}
                                            options={providers}
                                            placeholder="Select a provider"
                                            error={errors.provider_id}
                                            onRetry={loadProviders}
                                        />
                                    </Field>

                                    <Field label="Scope Level" required error={errors.level}>
                                        <select className={`cm-input ${errors.level ? "error" : ""}`} value={form.level} onChange={set("level")}>
                                            {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                                        </select>
                                        {/* <div className="cm-help">{LEVELS.find((l) => l.value === form.level)?.description}</div> */}
                                    </Field>

                                    {form.level === "category" && (
                                        <Field label="Product Type" required error={errors.product_type_name}>
                                            <SelectField
                                                value={form.product_type_name}
                                                onChange={set("product_type_name")}
                                                status={categoriesState}
                                                options={categories}
                                                placeholder="Select a category"
                                                error={errors.product_type_name}
                                                onRetry={loadCategories}
                                            />
                                        </Field>
                                    )}

                                    {form.level === "item" && (
                                        <Field label="Item" required error={errors.item_id}>
                                            <SelectField
                                                value={form.item_id}
                                                onChange={set("item_id")}
                                                status={form.provider_id ? itemsState : "idle"}
                                                options={items}
                                                placeholder={form.provider_id ? "Select an item" : "Select a provider first"}
                                                disabled={!form.provider_id}
                                                error={errors.item_id}
                                                onRetry={() => loadItems(form.provider_id)}
                                            />
                                        </Field>
                                    )}

                                    <Field label="Margin Type">
                                        <select className="cm-input" value={form.channel_margin_type} onChange={set("channel_margin_type")}>
                                            <option value="percent">Percentage</option>
                                            <option value="amount">Fixed Amount</option>
                                        </select>
                                    </Field>

                                    <Field label="Margin Value" required error={errors.channel_margin_value}>
                                        <div style={{ position: "relative" }}>
                                            <input
                                                className={`cm-input ${errors.channel_margin_value ? "error" : ""}`}
                                                value={form.channel_margin_value}
                                                onChange={set("channel_margin_value")}
                                                placeholder={form.channel_margin_type === "percent" ? "e.g. 2.50" : "e.g. 12.50"}
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                style={{ paddingRight: 55 }}
                                            />
                                            <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 12, fontWeight: 600, color: "#9ca3af" }}>
                                                {form.channel_margin_type === "percent" ? "%" : "INR"}
                                            </span>
                                        </div>
                                    </Field>

                                    {/* <Field label="Title">
                                        <input className="cm-input" value={form.title} onChange={set("title")} placeholder="e.g. Default provider margin" />
                                    </Field> */}
                                </div>

                                <div className="cm-divider" />

                                <div className="cm-section-heading">
                                    <h3 className="cm-section-heading-title">Optional Filters</h3>
                                    <p className="cm-section-heading-text">Leave these fields blank if the margin should apply broadly.</p>
                                </div>

                                <div className="cm-form-grid-4">
                                    <Field label="Buyer App">
                                        <input className="cm-input" value={form.buyer_app_id} onChange={set("buyer_app_id")} placeholder="bap.example.com" />
                                    </Field>
                                    {/* <Field label="Domain">
                                        <input className="cm-input" value={form.domain} onChange={set("domain")} placeholder="retail" />
                                    </Field> */}
                                    {/* <Field label="City">
                                        <input className="cm-input" value={form.city} onChange={set("city")} placeholder="Lucknow" />
                                    </Field> */}
                                    {/* <Field label="Valid From">
                                        <input className="cm-input" type="date" value={form.valid_from} onChange={set("valid_from")} />
                                    </Field>
                                    <Field label="Valid To">
                                        <input className="cm-input" type="date" value={form.valid_to} onChange={set("valid_to")} />
                                    </Field> */}
                                </div>

                                <div className="cm-actions">
                                    {form.id && (
                                        <button className="cm-btn cm-btn-secondary" onClick={() => { resetForm(); setTab("list"); }}>Cancel</button>
                                    )}
                                    <button className="cm-btn cm-btn-primary" onClick={handleSubmit} disabled={submitting}>
                                        {submitting && <FiLoader className="cm-spin" size={14} />}
                                        {form.id ? "Save Changes" : "Create Margin"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="cm-card">
                            <div className="cm-card-header">
                                <div>
                                    <h2 className="cm-section-title">All Channel Margins</h2>
                                    <p className="cm-section-description">Filter by scope, channel, or status.</p>
                                </div>
                                <div className="cm-filter-toggle">
                                    <button className="cm-btn cm-btn-ghost" onClick={() => setShowFilters((v) => !v)}>
                                        <FiFilter size={13} /> Filters
                                    </button>
                                    {activeFilterCount > 0 && <span className="cm-filter-badge">{activeFilterCount}</span>}
                                </div>
                            </div>

                            {showFilters && (
                                <div className="cm-filter-panel">
                                    <div className="cm-form-grid-6">
                                        <Field label="Provider">
                                            <select className="cm-input" value={filters.provider_id} onChange={setFilter("provider_id")}>
                                                <option value="">All providers</option>
                                                {providers.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                            </select>
                                        </Field>
                                        <Field label="Level">
                                            <select className="cm-input" value={filters.level} onChange={setFilter("level")}>
                                                <option value="">All levels</option>
                                                {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                                            </select>
                                        </Field>
                                        <Field label="Buyer App">
                                            <input className="cm-input" value={filters.buyer_app_id} onChange={setFilter("buyer_app_id")} placeholder="buyerapp.example.com" />
                                        </Field>
                                        <Field label="Product Type">
                                            <SelectField
                                                value={filters.product_type_name}
                                                onChange={setFilter("product_type_name")}
                                                status={categoriesState}
                                                options={categories}
                                                placeholder="All product types"
                                                onRetry={loadCategories}
                                            />
                                        </Field>
                                        <Field label="Item">
                                            <SelectField
                                                value={filters.item_id}
                                                onChange={setFilter("item_id")}
                                                status={filters.provider_id ? itemsState : "idle"}
                                                options={items}
                                                placeholder={filters.provider_id ? "All items" : "Select a provider first"}
                                                disabled={!filters.provider_id}
                                                onRetry={() => loadItems(filters.provider_id)}
                                            />
                                        </Field>
                                        <Field label="Status">
                                            <select className="cm-input" value={filters.status} onChange={setFilter("status")}>
                                                {STATUS_FILTER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                            </select>
                                        </Field>
                                    </div>
                                    <div className="cm-filter-actions">
                                        <button className="cm-btn cm-btn-secondary" onClick={clearFilters}>
                                            <FiX size={13} /> Clear
                                        </button>
                                        <button className="cm-btn cm-btn-primary" onClick={applyFilters}>
                                            Apply Filters
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div className="cm-table-wrapper">
                                <table className="cm-table">
                                    <thead>
                                        <tr>
                                            <th>Scope</th>
                                            <th>Buyer App</th>
                                            <th>Margin Type</th>
                                            <th>Value</th>
                                            <th>Status</th>
                                            <th style={{ textAlign: "right" }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {listLoading ? (
                                            <tr><td colSpan={6} className="cm-empty">Loading margins…</td></tr>
                                        ) : listError ? (
                                            <tr>
                                                <td colSpan={6} className="cm-empty">
                                                    <div style={{ color: "#dc2626", fontWeight: 600, marginBottom: 6 }}>Couldn't load margins</div>
                                                    <div style={{ fontSize: 12 }}>{listError}</div>
                                                    <button className="cm-btn cm-btn-secondary" style={{ marginTop: 12 }} onClick={() => fetchMargins()}>Retry</button>
                                                </td>
                                            </tr>
                                        ) : margins.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="cm-empty">
                                                    <div className="cm-empty-icon"><FiList size={20} /></div>
                                                    <div style={{ color: "#374151", fontWeight: 600, fontSize: 13 }}>No channel margins found</div>
                                                    <div style={{ marginTop: 5, fontSize: 12 }}>
                                                        {activeFilterCount > 0 ? "Try adjusting or clearing your filters." : "Create your first channel margin to get started."}
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            margins.map((m) => {
                                                const providerLabel = providers.find((p) => p.value === m.provider_id)?.label || m.provider_id;
                                                const extra = localExtras[m.id];
                                                return (
                                                    <tr key={m.id} style={{ opacity: m.status ? 1 : 0.55 }}>
                                                        <td>
                                                            <div className="cm-scope-title">
                                                                {providerLabel}
                                                                {m.level === "category" && ` › ${m.product_type_name}`}
                                                                {m.level === "item" && ` › ${m.item_id}`}
                                                            </div>
                                                            <div className="cm-scope-meta">{cap(m.level)}{extra?.title ? ` · ${extra.title}` : ""}</div>
                                                        </td>
                                                        <td style={{ color: m.buyer_app_id ? "#374151" : "#9ca3af" }}>
                                                            {m.buyer_app_id || "All channels"}
                                                        </td>
                                                        <td><span style={{ color: "#6b7280" }}>{cap(m.channel_margin_type)}</span></td>
                                                        <td><span className="cm-value">{fmtValue(m.channel_margin_value, m.channel_margin_type)}</span></td>
                                                        <td>
                                                            <span className={`cm-status ${m.status ? "active" : "disabled"}`}>
                                                                <span className="cm-status-dot" />
                                                                {m.status ? "ACTIVE" : "DISABLED"}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="cm-actions-cell">
                                                                <ToggleSwitch
                                                                    checked={!!m.status}
                                                                    onChange={() => handleToggle(m)}
                                                                    title={m.status ? "Disable" : "Enable"}
                                                                />
                                                                <button className="cm-icon-btn" title="Edit" onClick={() => handleEdit(m)}>
                                                                    <FiEdit2 size={14} />
                                                                </button>
                                                                <button className="cm-icon-btn danger" title="Delete" onClick={() => handleDelete(m.id)}>
                                                                    <FiTrash2 size={14} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {toast && <div className="cm-toast">{toast}</div>}
            </div>
        </ThemeMaker>
    );
}

function Field({ label, required, error, children }) {
    return (
        <div className="cm-field">
            <label className="cm-label">{label}{required && <span className="cm-required">*</span>}</label>
            {children}
            {error && <div className="cm-error">{error}</div>}
        </div>
    );
}

// Dropdown that reflects loading / error / loaded states for API-backed lists.
function SelectField({ value, onChange, status, options, placeholder, disabled, error, onRetry }) {
    if (status === "loading") {
        return (
            <div className="cm-input cm-select-status" style={{ color: "#9ca3af" }}>
                <span><FiLoader className="cm-spin" size={13} style={{ marginRight: 6, verticalAlign: "middle" }} />Loading…</span>
            </div>
        );
    }
    if (status === "error") {
        return (
            <div className="cm-input cm-select-status" style={{ color: "#dc2626", borderColor: "#fca5a5" }}>
                <span style={{ fontSize: 12 }}>Couldn't load list</span>
                <button onClick={onRetry} style={{ fontSize: 12, fontWeight: 600, color: "#4f5fde", background: "none", border: "none", cursor: "pointer" }}>
                    Retry
                </button>
            </div>
        );
    }
    return (
        <select className={`cm-input ${error ? "error" : ""}`} value={value} onChange={onChange} disabled={disabled}>
            <option value="">{placeholder}</option>
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
    );
}

// Small controlled toggle switch — visually replaces the old power icon
// button but keeps the same click target size in the actions cell.
function ToggleSwitch({ checked, onChange, title, disabled }) {
    return (
        <label className="cm-toggle" title={title}>
            <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
            <span className="cm-toggle-track" />
            <span className="cm-toggle-thumb" />
        </label>
    );
}